import {
	type DepositableV3Assets,
	DepositableV3AssetsBase,
	V3_SYNTH_KEYS,
	ZERO_WEI,
} from '@kwenta/sdk/constants'
import { PerpsProvider, SynthAssetKeysV3, type TokenTickersAndEth } from '@kwenta/sdk/types'
import {
	DepositTokenToSynth,
	depositableAssetToToken,
	formatCurrency,
	formatDollars,
	formatNumber,
	formatPercent,
	stripZeros,
	truncateNumbers,
} from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ValueType } from 'react-select'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxContainer, InfoBoxRow } from 'components/InfoBox'
import InputWithAssetSelector from 'components/Input/InputWithAssetSelector'
import SegmentedControl from 'components/SegmentedControl'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { DEFAULT_USDC_DECIMALS } from 'constants/defaults'
import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import isEqual from 'lodash/isEqual'
import { selectShowModalWithParams } from 'state/app/selectors'
import { selectBalances, selectBalancesAllProviders } from 'state/balances/selectors'
import { selectProviderNetwork, selectSnxV3Provider } from 'state/futures/common/selectors'
import { setQueryStatus, setSnxV3DebtPaymentQuote } from 'state/futures/reducer'
import { selectIsSubmittingIsolatedTransfer, selectQueryStatuses } from 'state/futures/selectors'
import { quoteDebtPaymentSwap, withdrawCrossMargin } from 'state/futures/snxPerpsV3/actions'
import {
	selectCollateralBalances,
	selectCrossMarginActivePositions,
	selectCrossMarginMarginInfo,
	selectDebtPaymentQuote,
	selectV3DepositOptions,
	selectV3SynthPrices,
	selectWithdrawableCrossMargin,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { FetchStatus } from 'state/types'
import { selectAbstractionWalletEnabled, selectIsSubAccountMode } from 'state/wallet/selectors'

const PERCENT_OPTIONS = ['25%', '50%', '75%', '100%']

const FullWidthSelectorButtons = ({
	onSelect,
	options,
}: { onSelect: (index: number) => void; options: string[] }) => {
	return (
		<SelectorButtonsContainer>
			<FullWidthSegmentedControl onChange={onSelect} styleType="pill-button" values={options} />
		</SelectorButtonsContainer>
	)
}

type Props = {
	defaultAsset?: DepositableV3Assets
}

const WithdrawCrossMargin = memo(({ defaultAsset }: Props) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const submitting = useAppSelector(selectIsSubmittingIsolatedTransfer)
	const collateralBalances = useAppSelector(selectCollateralBalances)
	const withdrawableMargin = useAppSelector(selectWithdrawableCrossMargin)
	const indexPrices = useAppSelector(selectV3SynthPrices)
	const activePositions = useAppSelector(selectCrossMarginActivePositions)
	const { debt } = useAppSelector(selectCrossMarginMarginInfo)
	const provider = useAppSelector(selectSnxV3Provider)
	const modalParams = useAppSelector(selectShowModalWithParams)?.params
	const depositOptions = useAppSelector(selectV3DepositOptions)
	const walletBalances = useAppSelector(selectBalances)
	const walletBalancesAllProviders = useAppSelector(selectBalancesAllProviders, isEqual)
	const debtPaymentQuote = useAppSelector(selectDebtPaymentQuote)
	const quoteQueryStatus = useAppSelector(selectQueryStatuses).quote_debt_payment
	const providerChainId = useAppSelector(selectProviderNetwork)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)

	const { trackWithdrawFlow } = useAnalytics()

	const [debtSettlementMethod, setDebtSettlementMethod] = useState(0)

	const baseAsset = useMemo(() => DepositableV3AssetsBase.USDC, [])

	const withdrawOptions = useMemo(() => {
		return depositOptions.filter((o) => {
			const asset = DepositTokenToSynth[provider][o]
			return asset && wei(collateralBalances[asset] ?? 0).gt(0)
		})
	}, [depositOptions, provider, collateralBalances])

	const [selectedAsset, setSelectedAsset] = useState<DepositableV3Assets>(() => {
		if (defaultAsset) return defaultAsset

		return withdrawOptions[0] ?? DepositableV3AssetsBase.USDC
	})
	const [amount, setAmount] = useState('')

	const canSettleDebtWithCollateral = useMemo(
		() => selectedAsset !== DepositableV3AssetsBase.USDC,
		[selectedAsset]
	)

	const assetDecimals = useMemo(() => {
		return selectedAsset === 'USDC' ? DEFAULT_USDC_DECIMALS : 18
	}, [selectedAsset])

	const onSelectPercent = useCallback(
		(index: number) => {
			const percent = PERCENT_OPTIONS[index].replace('%', '')
			const margin = wei(maxWithdraw).div(100).mul(percent)
			setAmount(stripZeros(truncateNumbers(margin, assetDecimals)))
		},
		[assetDecimals]
	)

	const balance = useMemo(
		() =>
			walletBalancesAllProviders?.[providerChainId]?.[
				selectedAsset.toUpperCase() as TokenTickersAndEth
			]?.balance ?? ZERO_WEI,
		[walletBalancesAllProviders, providerChainId, selectedAsset]
	)

	useEffect(() => {
		if (!canSettleDebtWithCollateral) {
			setDebtSettlementMethod(1)
		}
	}, [canSettleDebtWithCollateral])

	useEffect(() => {
		const asset = modalParams?.asset
		if (V3_SYNTH_KEYS.includes(asset)) {
			setSelectedAsset(asset)
		}
		// Reset query status on mount
		dispatch(setQueryStatus({ key: 'quote_debt_payment', status: FetchStatus.Idle }))
		dispatch(setSnxV3DebtPaymentQuote({ quote: undefined }))
	}, [modalParams?.asset, dispatch])

	// biome-ignore lint/correctness/useExhaustiveDependencies: debt as string
	useEffect(() => {
		let interval: NodeJS.Timeout
		if (Number(debt) !== 0 && canSettleDebtWithCollateral && debtSettlementMethod === 0) {
			const getQuote = () => {
				dispatch(
					quoteDebtPaymentSwap({
						asset: depositableAssetToToken(selectedAsset),
						debtAmount: wei(debt),
					})
				)
			}

			interval = setInterval(getQuote, 10000)
			getQuote()
		}
		return () => clearInterval(interval)
	}, [debt, selectedAsset, debtSettlementMethod])

	const maxWithdrawUSD = useMemo(() => {
		// Prevent withdraw failures when open positions result in dynamic withdrawable margin
		const max =
			activePositions.length > 0
				? wei(withdrawableMargin).sub(wei(withdrawableMargin).mul(0.001))
				: wei(withdrawableMargin)
		return max.gt(0) ? max : wei(0)
	}, [withdrawableMargin, activePositions.length])

	const selectedSynthAsset = useMemo(() => {
		const asset = DepositTokenToSynth[provider][selectedAsset]
		if (asset === SynthAssetKeysV3.sUSDC && provider === PerpsProvider.SNX_V3_BASE)
			return SynthAssetKeysV3.USDx
		return asset ?? SynthAssetKeysV3.sUSDC
	}, [selectedAsset, provider])

	const selectedAssetBalance = useMemo(() => {
		return wei(collateralBalances[selectedSynthAsset] ?? '0')
	}, [selectedSynthAsset, collateralBalances])

	const selectedAssetPrice = useMemo(() => {
		const price = indexPrices[selectedSynthAsset]
		return price ?? wei(0)
	}, [indexPrices, selectedSynthAsset])

	const maxWithdraw = useMemo(() => {
		if (selectedSynthAsset === SynthAssetKeysV3.USDx) {
			return maxWithdrawUSD.gt(selectedAssetBalance) ? selectedAssetBalance : maxWithdrawUSD
		}
		if (!activePositions.length) {
			// If there are no open positions the user can withdraw all of their collateral
			return selectedAssetBalance
		}
		const maxWithdrawAsset = selectedAssetPrice.gt(0)
			? maxWithdrawUSD.div(selectedAssetPrice)
			: wei(0)
		return maxWithdrawAsset.gt(selectedAssetBalance) ? selectedAssetBalance : maxWithdrawAsset
	}, [
		maxWithdrawUSD,
		selectedSynthAsset,
		selectedAssetPrice,
		selectedAssetBalance,
		activePositions.length,
	])

	const debtPaymentRequired = useMemo(() => {
		return wei(debt).gt(0)
	}, [debt])

	const baseAssetWalletBal = useMemo(() => {
		return wei(walletBalances[baseAsset]?.balance ?? 0)
	}, [walletBalances, baseAsset])

	const directDebtPaymentPossible = useMemo(() => {
		return baseAssetWalletBal.gte(debt)
	}, [baseAssetWalletBal, debt])

	const withdrawableNative = useMemo(() => {
		if (!debtPaymentQuote) return wei(amount || 0)
		const withdrawableAmount = wei(amount || 0).sub(wei(debtPaymentQuote.debtPaymentCollateral))
		return withdrawableAmount.lt(0) ? wei(0) : withdrawableAmount
	}, [amount, debtPaymentQuote])

	const quotedPrice = useMemo(() => {
		if (Number(debt) > 0 && selectedAsset === DepositableV3AssetsBase.USDC) return '1'
		return debtPaymentQuote?.quotedPrice
	}, [debtPaymentQuote?.quotedPrice, debt, selectedAsset])

	const minWithdrawNative = useMemo(() => {
		if (!quotedPrice) return wei(0)
		return wei(debt).add(1).div(quotedPrice).mul(1.01)
	}, [debt, quotedPrice])

	const inputInvalid = useMemo(() => {
		if (Number(amount) > maxWithdraw.toNumber()) return true
		if (debtSettlementMethod === 0) {
			return amount !== '' && wei(amount).lt(minWithdrawNative)
		}
	}, [debtSettlementMethod, amount, minWithdrawNative, maxWithdraw])

	const withdrawDisabled = useMemo(() => {
		if (
			isSubAccountMode ||
			inputInvalid ||
			!amount ||
			Number(amount) === 0 ||
			submitting ||
			maxWithdraw.lt(amount)
		) {
			return true
		}
		if (
			debtPaymentRequired &&
			debtSettlementMethod === 0 &&
			selectedAsset !== DepositableV3AssetsBase.USDC
		) {
			return quoteQueryStatus?.status !== FetchStatus.Success
		} else if (debtPaymentRequired && debtSettlementMethod === 1 && !directDebtPaymentPossible) {
			return true
		}

		return false
	}, [
		isSubAccountMode,
		inputInvalid,
		amount,
		submitting,
		maxWithdraw,
		debtPaymentRequired,
		directDebtPaymentPossible,
		debtSettlementMethod,
		quoteQueryStatus,
		selectedAsset,
	])

	const onSelectAsset = useCallback((asset: ValueType<any>) => {
		setAmount('')
		setSelectedAsset(asset as DepositableV3Assets)
	}, [])

	const handleSetMax = useCallback(() => {
		setAmount(stripZeros(truncateNumbers(maxWithdraw, assetDecimals)))
	}, [maxWithdraw, assetDecimals])

	const onWithdraw = useCallback(async () => {
		const token = selectedAsset
		trackWithdrawFlow(EVENT_NAME.SUBMIT_WITHDRAW)
		await dispatch(
			withdrawCrossMargin({
				amount: wei(amount),
				asset: selectedSynthAsset,
				unwrapToToken: token,
				payDebtWithCollateral: debtSettlementMethod === 0,
			})
		)
	}, [dispatch, amount, selectedSynthAsset, selectedAsset, debtSettlementMethod, trackWithdrawFlow])

	const openCurve = useCallback(() => {
		window.open(
			'https://curve.fi/#/arbitrum/swap?from=0xff970a61a04b1ca14834a43f5de4533ebddb5cc8&to=0xb2f30a7c980f052f02563fb518dcc39e6bf38175',
			'_blank'
		)
	}, [])

	const directPaymentErrorMessage = useMemo(() => {
		return `Insufficent USDx balance (${formatDollars(
			baseAssetWalletBal
		)}) to pay debt (${formatDollars(debt)}). You will need ${formatDollars(
			debt
		)} in your wallet for debt repayment in order to withdraw collateral.`
	}, [baseAssetWalletBal, debt])

	const renderDebtSettlementInfo = useMemo(() => {
		if (!debtPaymentRequired) return
		if (debtSettlementMethod === 1 || !canSettleDebtWithCollateral)
			return (
				<>
					<InfoBoxRow
						title={`${baseAsset} Wallet Balance`}
						textValue={formatNumber(baseAssetWalletBal, {
							suggestDecimals: true,
						})}
					/>
					{!directDebtPaymentPossible && (
						<ErrorView messageType="error" message={directPaymentErrorMessage} />
					)}
				</>
			)

		return (
			<>
				<InfoBoxRow
					title={`${selectedAsset} (USDC) amount`}
					textValue={`${formatCurrency(
						debtPaymentQuote?.token ?? selectedAsset,
						debtPaymentQuote?.debtPaymentCollateral ?? 0,
						{
							suggestDecimals: true,
						}
					)} (${formatDollars(debtPaymentQuote?.debtPaymentUsdc ?? 0, {
						suggestDecimals: true,
					})})`}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.margin.modal.estimated-price')}
					textValue={formatNumber(debtPaymentQuote?.quotedPrice ?? 0)}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.margin.modal.predicted-slippage')}
					color={wei(debtPaymentQuote?.priceImpact || 0).gt(0.01) ? 'negative' : 'primary'}
					textValue={formatPercent(debtPaymentQuote?.priceImpact ?? 0)}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.margin.modal.withdraw-amount')}
					textValue={formatCurrency(debtPaymentQuote?.token ?? selectedAsset, withdrawableNative, {
						suggestDecimals: true,
					})}
				/>

				{wei(debtPaymentQuote?.priceImpact || 0).gt(0.01) && (
					<>
						<Spacer height={20} />
						<ErrorView
							messageType="warn"
							onClick={openCurve}
							message={`Debt payment with ${selectedAsset} incurs high slippage. You might want to consider purchasing some USDX to pay your debt instead.`}
						/>
					</>
				)}

				{quoteQueryStatus?.error && (
					<>
						<Spacer height={20} />
						<ErrorView messageType="error" message={quoteQueryStatus.error} />
					</>
				)}
			</>
		)
	}, [
		openCurve,
		debtPaymentRequired,
		debtSettlementMethod,
		debtPaymentQuote,
		t,
		baseAsset,
		selectedAsset,
		withdrawableNative,
		quoteQueryStatus,
		directPaymentErrorMessage,
		directDebtPaymentPossible,
		baseAssetWalletBal,
		canSettleDebtWithCollateral,
	])

	return (
		<FlexDivCol rowGap="20px">
			<FlexDivCol rowGap="10px">
				<BalanceContainer onClick={handleSetMax}>
					<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
					<BalanceText>
						<span>{formatNumber(maxWithdraw, { suggestDecimals: true, floor: true })}</span>
					</BalanceText>
				</BalanceContainer>
				<InputWithAssetSelector
					small={true}
					value={truncateNumbers(amount, assetDecimals)}
					setValue={setAmount}
					invalid={inputInvalid}
					leftLabel={
						wei(minWithdrawNative).gt(0) && debtSettlementMethod === 0 ? (
							<Body color={inputInvalid ? 'negative' : 'tertiary'}>
								Min: {formatNumber(minWithdrawNative, { suggestDecimals: true })}
							</Body>
						) : undefined
					}
					selectedAsset={selectedAsset}
					onSelectAsset={onSelectAsset}
					assets={withdrawOptions}
				/>
				<FullWidthSelectorButtons onSelect={onSelectPercent} options={PERCENT_OPTIONS} />
			</FlexDivCol>
			{wei(debt).gt(0) && (
				<InfoBoxContainer>
					<Spacer height={5} />
					<FlexDivRowCentered>
						<Body size="large" weight="bold">
							Your Debt
						</Body>
						<Body weight="bold" size="large" color="negative">
							{formatNumber(debt, { suggestDecimals: true })} <DebtTicker>{baseAsset}</DebtTicker>
						</Body>
					</FlexDivRowCentered>
					<Spacer height={15} />
					{canSettleDebtWithCollateral && (
						<>
							<SegmentedControl
								values={['Settle with Collateral', `Settle with ${baseAsset}`]}
								selectedIndex={debtSettlementMethod}
								onChange={(index) => setDebtSettlementMethod(index)}
							/>
							<Spacer height={20} />
						</>
					)}
					{renderDebtSettlementInfo}
				</InfoBoxContainer>
			)}

			{abstractionWalletEnabled && (
				<>
					<Divider height={1} divider />
					<FlexDivCol rowGap="10px">
						<FlexDivRowCentered>
							<Body color="secondary">
								{t('futures.market.trade.margin.modal.transfer.perps-v3-account')}
							</Body>
							<Body color="secondary">
								{inputInvalid ||
								!amount ||
								Number(amount) === 0 ||
								maxWithdraw.lt(amount) ||
								debtPaymentRequired
									? formatNumber(wei(withdrawableMargin), {
											suggestDecimals: true,
											floor: true,
										})
									: formatNumber(wei(withdrawableMargin).sub(wei(amount === '' ? '0' : amount)), {
											suggestDecimals: true,
											floor: true,
										})}{' '}
								USD
							</Body>
						</FlexDivRowCentered>
						<FlexDivRowCentered>
							<Body color="secondary">
								{t('futures.market.trade.margin.modal.transfer.synthetix-account')}
							</Body>
							<Body color="primary">
								{inputInvalid ||
								!amount ||
								Number(amount) === 0 ||
								maxWithdraw.lt(amount) ||
								debtPaymentRequired
									? formatNumber(wei(balance), {
											suggestDecimals: true,
											floor: true,
										})
									: formatNumber(wei(balance).add(wei(amount === '' ? '0' : amount)), {
											suggestDecimals: true,
											floor: true,
										})}{' '}
								{selectedAsset}
							</Body>
						</FlexDivRowCentered>
					</FlexDivCol>
				</>
			)}

			<TransactionButton
				data-testid="futures-market-trade-deposit-withdraw-button"
				loading={submitting || quoteQueryStatus?.status === FetchStatus.Loading}
				fullWidth
				disabled={withdrawDisabled}
				onClick={onWithdraw}
				variant="solid-highlight"
			>
				{abstractionWalletEnabled
					? t('futures.market.trade.margin.modal.withdraw.transfer-button')
					: t('futures.market.trade.margin.modal.withdraw.button')}
			</TransactionButton>

			{isSubAccountMode && (
				<ErrorView
					messageType="warn"
					message={t('futures.market.trade.confirmation.modal.disabled-withdraw-delegation')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</FlexDivCol>
	)
})

export default WithdrawCrossMargin

const BalanceContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	p {
		margin: 0;
	}
`

const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const DebtTicker = styled.span`
	font-weight: 300;
	color: ${({ theme }) => theme.colors.selectedTheme.text.label};
`

const Divider = styled(Spacer)`
	width: 100%;
`

const SelectorButtonsContainer = styled.div`
	width: 100%;
	display: flex;
`

const FullWidthSegmentedControl = styled(SegmentedControl)`
	width: 100%;
	
	& > div {
		width: 100%;
		display: flex;
	}
	
	& button {
		flex: 1;
		font-size: 12px;
		height: 25px;
	}
`
